import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MapPin, Phone, Mail, Clock, User, Instagram } from 'lucide-react';
import { contactData, clubData, classesData } from '../data/mock';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    class: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message
    const message = `Hola! Vull reservar una plaça:\n\nNom: ${formData.name}\nEmail: ${formData.email}\nTelèfon: ${formData.phone}\nClasse: ${formData.class}\n\nMissatge: ${formData.message}`;
    const whatsappUrl = `https://wa.me/34${contactData.phone.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    toast.success('Redirigint a WhatsApp...');
    
    setFormData({
      name: '',
      email: '',
      phone: '',
      class: '',
      message: '',
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            <span className="text-[#39FF14]">Contacta</span> Amb Nosaltres
          </h2>
          <p className="text-xl text-gray-400">
            Reserva la teva plaça o demana més informació
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="bg-gray-900/50 border-2 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Informació de Contacte</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#39FF14]/10 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-[#39FF14]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Adreça</h4>
                    <p className="text-gray-400">{contactData.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#39FF14]/10 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-[#39FF14]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Telèfon</h4>
                    <a href={`tel:${contactData.phone}`} className="text-[#39FF14] hover:underline">
                      {contactData.phone}
                    </a>
                    <p className="text-gray-500 text-sm">{contactData.phoneContact}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#39FF14]/10 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-[#39FF14]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Email</h4>
                    <a href={`mailto:${contactData.email}`} className="text-[#39FF14] hover:underline break-all">
                      {contactData.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#39FF14]/10 p-3 rounded-lg">
                    <Clock className="w-6 h-6 text-[#39FF14]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Horari</h4>
                    <p className="text-gray-400">{contactData.hours}</p>
                    <p className="text-gray-500 text-sm">{contactData.daysOpen}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#39FF14]/10 p-3 rounded-lg">
                    <User className="w-6 h-6 text-[#39FF14]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{contactData.role}</h4>
                    <p className="text-gray-400">{contactData.president}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-800">
                  <a
                    href={clubData.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-[#39FF14] hover:text-[#2EDD0F] transition-colors"
                  >
                    <Instagram className="w-6 h-6" />
                    <span className="font-bold">@training_clubesportiuorpi</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reservation Form */}
          <Card className="bg-gray-900/50 border-2 border-[#39FF14]">
            <CardHeader>
              <CardTitle className="text-white text-2xl">Reserva una Plaça</CardTitle>
              <p className="text-gray-400 text-sm mt-2">
                Omple el formulari i et contactarem per WhatsApp
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-white">Nom complet *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white focus:border-[#39FF14]"
                    placeholder="El teu nom"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-white">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white focus:border-[#39FF14]"
                    placeholder="email@exemple.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-white">Telèfon *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white focus:border-[#39FF14]"
                    placeholder="697 377 826"
                  />
                </div>

                <div>
                  <Label htmlFor="class" className="text-white">Classe (opcional)</Label>
                  <select
                    id="class"
                    value={formData.class}
                    onChange={(e) => setFormData({ ...formData, class: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 focus:border-[#39FF14] focus:outline-none"
                  >
                    <option value="">Selecciona una classe</option>
                    {classesData.map((classItem) => (
                      <option key={classItem.id} value={`${classItem.name} - ${classItem.day} ${classItem.time}`}>
                        {classItem.name} - {classItem.day} {classItem.time}
                      </option>
                    ))}
                    <option value="Soci nou">Soci nou</option>
                    <option value="Més informació">Més informació</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-white">Missatge (opcional)</Label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 focus:border-[#39FF14] focus:outline-none min-h-[100px]"
                    placeholder="Escriu el teu missatge aquí..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#39FF14] hover:bg-[#2EDD0F] text-black font-bold py-3 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Enviar per WhatsApp
                </button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
